import React, { ReactElement, useRef, useCallback, useState, useEffect, useImperativeHandle } from 'react';
import styled from 'styled-components/macro';
import { Instance as TippyInstance, Placement } from 'tippy.js';
import Tippy, { TippyProps } from '@tippyjs/react/headless';
import { PopoverPlacement } from 'shared/components/Dropdown';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { LazyTippy } from '../LazyTippy';

// TODO: Genericize this Popover component to use as foundation for existing Dropdown component

interface IPopoverProps extends TippyProps {
  anchor: ReactElement;
  isOpen?: boolean;
  placement?: PopoverPlacement;
  xOffset?: number;
  yOffset?: number;
  children: ReactElement;
  isLazy?: boolean;
}

export { PopoverPlacement };

export interface IPopoverRef {
  show: () => void;
  hide: () => void;
}

const Popover = React.forwardRef(
  (
    {
      anchor,
      isOpen,
      placement = PopoverPlacement.RightEnd,
      children,
      xOffset,
      yOffset,
      isLazy,
      ...rest
    }: IPopoverProps,
    ref,
  ): JSX.Element => {
    const [tippyInstance, setTippyInstance] = useState<TippyInstance>();
    const dropdownContainerRef = useRef<HTMLDivElement | null>(null);
    const { isMobile } = useResolutionProvider();
    const defaultXOffset = -18;
    const defaultYOffset = -8;
    useImperativeHandle(
      ref,
      (): IPopoverRef => ({
        show: (): void => {
          tippyInstance?.show();
        },
        hide: (): void => {
          tippyInstance?.hide();
        },
      }),
    );

    const onCreate = useCallback(
      (instance) => {
        setTippyInstance(instance);
      },
      [setTippyInstance],
    );

    useEffect(() => {
      if (!tippyInstance) return;
      const { isShown } = tippyInstance.state;

      if (isOpen && !isShown) {
        tippyInstance.show();
      } else if (!isOpen && isShown) {
        tippyInstance.hide();
      }
    }, [isOpen, tippyInstance]);

    const renderDropdown = (attrs: {
      'data-placement': Placement;
      'data-reference-hidden'?: string | undefined;
      'data-escaped'?: string | undefined;
    }): JSX.Element => {
      return (
        <PopoverContainer {...attrs} ref={dropdownContainerRef}>
          {children}
        </PopoverContainer>
      );
    };

    const offset: [number, number] = [xOffset || defaultXOffset, yOffset || defaultYOffset];
    const allProps = {
      interactive: true,
      appendTo: document.getElementById('root') || undefined,
      placement: !isMobile ? placement : PopoverPlacement.TopEnd,
      render: renderDropdown,
      onCreate: onCreate,
      trigger: 'click',
      offset: offset,
      ...rest,
    };

    if (isLazy) {
      return <LazyTippy {...allProps}>{anchor}</LazyTippy>;
    }
    return <Tippy {...allProps}>{anchor}</Tippy>;
  },
);

Popover.displayName = 'Popover';

const PopoverContainer = styled.div`
  min-width: 200px;
  width: auto;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  border: 1px solid #eee;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 6px 0 rgba(20, 79, 90, 0.23);
  background-color: var(--chakra-colors-background-default);

  @media (max-height: 500px) {
    max-height: 220px;
    overflow: auto;
  }

  & div > div > :hover {
  }
`;

export default Popover;
